/**
 * Question Set Player — grades MCQ, Match, FITB (dropdown / type), and Ordering
 */

import * as React from 'react';
import {
    cn,
    Progress,
    Button,
    type ElementChildLabels,
    type ElementPlayerProps,
    useIsMobile,
} from '@ai-content-hub/content-kit';
import { useLocale, useSinglePageNavPlacement } from '@ai-content-hub/content-core';
import type { ContentConfig } from '@ai-content-hub/content-core';
import {
    CompositePlayerGroupedItemsBody,
    orderItemsByQuestionsSidebarRows,
    useCompositePlayerSidebarLayout,
    ContentPlayerSplitLayout,
} from '@ai-content-hub/content-core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
    MCQPlayer,
    MatchPlayer,
    FITBPlayer,
    OrderingPlayer,
    PagePlayer,
    defaultPageData,
    isMcqAnswered,
    type MCQElementData,
    type MCQAnswer,
    type MatchElementData,
    type MatchAnswer,
    type FITBElementData,
    type FITBAnswer,
    type OrderingElementData,
    type OrderingAnswer,
    type PageElementData,
} from '@ai-content-hub/content-elements';
import type {
    QuestionSetData,
    QuestionSetItem,
    QuestionSetConfig,
    QuestionSetScoringConfig,
    QuestionSetLayoutConfig,
} from './schema';
import { normalizeQuestionSetLayoutConfig } from './layout';
import { fetchPassageMarkdown, mergePassageIntoPageContent } from './passageUrl';
import { gradeQuestionSet, type QuestionSetItemResult } from './scorer';
import trim from 'lodash/trim';
import map from 'lodash/map';
import filter from 'lodash/filter';
import './question-set-player.css';

const SINGLE_PAGE_NAV_PAGE_SIZE = 15;
const JUMP_TO_CHILD_SCROLL_TOP_OFFSET_PX = 50;

const EMPTY_QUESTION_SET_ITEMS: QuestionSetItem[] = [];
const EMPTY_ITEM_RESULTS: QuestionSetItemResult[] = [];

function shallowAnswerMapsEqual(
    a: Record<string, unknown>,
    b: Record<string, unknown>
): boolean {
    if (a === b) {
        return true;
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) {
        return false;
    }
    for (const k of keysA) {
        if (a[k] !== b[k]) {
            return false;
        }
    }
    return true;
}

function getVerticalScrollParent(el: HTMLElement): HTMLElement | null {
    let parent: HTMLElement | null = el.parentElement;
    while (parent && parent !== document.documentElement) {
        if (parent === document.body) {
            parent = parent.parentElement;
            continue;
        }
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const canScrollY =
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
            parent.scrollHeight > parent.clientHeight + 1;
        if (canScrollY) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

/** Scroll so `el` sits `offsetPx` below the top of the nearest vertical scroller (or viewport). */
function scrollElementToTopOffset(el: HTMLElement, offsetPx: number): void {
    const scrollParent = getVerticalScrollParent(el);
    if (scrollParent) {
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta = elRect.top - parentRect.top - offsetPx;
        scrollParent.scrollBy({ top: delta, behavior: 'smooth' });
        return;
    }
    const elRect = el.getBoundingClientRect();
    window.scrollBy({ top: elRect.top - offsetPx, behavior: 'smooth' });
}


export interface QuestionSetPlayerProps {
    data: QuestionSetData;
    config?: QuestionSetConfig & {
        behavior?: {
            allowRetry?: boolean;
            maxAttempts?: number;
            shuffleChoices?: boolean;
            showCorrectAnswer?: boolean;
            showExplanation?: boolean;
            instantFeedback?: boolean;
            examMode?: boolean;
        };
        scoring?: {
            max?: number;
            partialCreditEnabled?: boolean;
            passingScore?: number;
        };
    };
    onAnswer?: (answer: Record<string, unknown>) => void;
    showFeedback?: boolean;
    isReview?: boolean;
    disabled?: boolean;
    className?: string;
    answer?: Record<string, unknown>;
    result?: {
        isCorrect?: boolean | null;
        score?: number;
        max?: number;
        details?: Record<string, unknown>;
    };
    language?: string;
}

function isMatchAnswered(data: MatchElementData, answer: unknown): boolean {
    if (answer == null || typeof answer !== 'object') return false;
    const m = answer as MatchAnswer;
    const matchItems = data.items ?? [];
    return matchItems.every((it) => {
        const v = m[it.id];
        return typeof v === 'string' && trim(v) !== '';
    });
}

function isFitbAnswered(data: FITBElementData, answer: unknown): boolean {
    const blanks = data.blanks ?? [];
    if (blanks.length === 0) return true;
    if (answer == null || typeof answer !== 'object') return false;
    const f = answer as FITBAnswer;
    return blanks.every((b) => {
        const v = f[b.id];
        return typeof v === 'string' && trim(v) !== '';
    });
}

function isOrderingAnswered(data: OrderingElementData, answer: unknown): boolean {
    if (!Array.isArray(answer)) return false;
    const orderItems = data.items ?? [];
    return answer.length === orderItems.length && answer.every((v) => typeof v === 'string' && trim(v) !== '');
}

function isItemAnswered(item: QuestionSetItem, raw: unknown): boolean {
    switch (item.type) {
        case 'mcq':
            return isMcqAnswered(raw);
        case 'match':
            return isMatchAnswered(item.data, raw);
        case 'fitb-dropdown':
        case 'fitb-type':
            return isFitbAnswered(item.data, raw);
        case 'ordering':
            return isOrderingAnswered(item.data, raw);
    }
}

type ChildNavEntry = {
    anchorKey: string;
    itemId: string;
    kind: 'mcq' | 'match' | 'fitb' | 'ordering';
    subId?: string;
};

function buildChildNavEntries(items: QuestionSetItem[]): ChildNavEntry[] {
    const entries: ChildNavEntry[] = [];
    for (const item of items) {
        switch (item.type) {
            case 'mcq':
                entries.push({ anchorKey: `${item.id}::mcq`, itemId: item.id, kind: 'mcq' });
                break;
            case 'match':
                for (const row of (item.data.items ?? [])) {
                    entries.push({
                        anchorKey: `${item.id}::match::${row.id}`,
                        itemId: item.id,
                        kind: 'match',
                        subId: row.id,
                    });
                }
                break;
            case 'fitb-dropdown':
            case 'fitb-type':
                for (let i = 0; i < (item.data.blanks ?? []).length; i++) {
                    const bid = `blank-${i}`;
                    entries.push({
                        anchorKey: `${item.id}::fitb::${bid}`,
                        itemId: item.id,
                        kind: 'fitb',
                        subId: bid,
                    });
                }
                break;
            case 'ordering':
                entries.push({ anchorKey: `${item.id}::ordering`, itemId: item.id, kind: 'ordering' });
                break;
        }
    }
    return entries;
}

function isNavEntryAnswered(entry: ChildNavEntry, answerMap: Record<string, unknown>): boolean {
    const raw = answerMap[entry.itemId];
    if (entry.kind === 'mcq') {
        return isMcqAnswered(raw);
    }
    if (entry.kind === 'match') {
        if (raw == null || typeof raw !== 'object') return false;
        const m = raw as MatchAnswer;
        const v = entry.subId ? m[entry.subId] : undefined;
        return typeof v === 'string' && trim(v) !== '';
    }
    if (entry.kind === 'fitb') {
        if (raw == null || typeof raw !== 'object') return false;
        const f = raw as FITBAnswer;
        const v = entry.subId ? f[entry.subId] : undefined;
        return typeof v === 'string' && trim(v) !== '';
    }
    if (entry.kind === 'ordering') {
        return Array.isArray(raw) && raw.length > 0;
    }
    return false;
}

function isChildNavEntryCorrectWhenGraded(
    entry: ChildNavEntry,
    itemResults: QuestionSetItemResult[]
): boolean {
    const itemResult = itemResults.find((r) => r.itemId === entry.itemId);
    if (itemResult == null) {
        return false;
    }
    if (entry.kind === 'mcq') {
        return itemResult.isCorrect === true;
    }
    if (entry.kind === 'match' && entry.subId != null) {
        const d = itemResult.details as { itemResults?: Record<string, boolean> } | undefined;
        return d?.itemResults?.[entry.subId] === true;
    }
    if (entry.kind === 'fitb' && entry.subId != null) {
        const d = itemResult.details as { perBlank?: { id: string; ok: boolean }[] } | undefined;
        const row = d?.perBlank?.find((p) => p.id === entry.subId);
        return row?.ok === true;
    }
    if (entry.kind === 'ordering') {
        return itemResult.isCorrect === true;
    }
    return false;
}

function buildElementChildLabels(
    item: QuestionSetItem,
    labelByAnchor: Map<string, number>
): ElementChildLabels | undefined {
    switch (item.type) {
        case 'mcq': {
            const n = labelByAnchor.get(`${item.id}::mcq`);
            return n !== undefined ? { mcqStem: n } : undefined;
        }
        case 'match': {
            const matchItem: Record<string, number> = {};
            for (const row of (item.data.items ?? [])) {
                const n = labelByAnchor.get(`${item.id}::match::${row.id}`);
                if (n !== undefined) {
                    matchItem[row.id] = n;
                }
            }
            return Object.keys(matchItem).length > 0 ? { matchItem } : undefined;
        }
        case 'fitb-dropdown':
        case 'fitb-type': {
            const fitbBlank: Record<string, number> = {};
            for (let i = 0; i < (item.data.blanks ?? []).length; i++) {
                const bid = `blank-${i}`;
                const n = labelByAnchor.get(`${item.id}::fitb::${bid}`);
                if (n !== undefined) {
                    fitbBlank[bid] = n;
                }
            }
            return Object.keys(fitbBlank).length > 0 ? { fitbBlank } : undefined;
        }
        case 'ordering':
            return undefined;
    }
}

function getQuestionSetChildNavButtonProps(options: {
    useGradeColors: boolean;
    answered: boolean;
    childCorrectWhenGraded: boolean;
}): {
    variant: 'default' | 'outline';
    className: string;
} {
    const base =
        'question-set-player-tab question-set-player-single-page-nav-num cursor-pointer min-w-9 font-medium transition-colors duration-200';
    const { useGradeColors, answered, childCorrectWhenGraded } = options;
    if (useGradeColors) {
        if (childCorrectWhenGraded) {
            return {
                variant: 'outline',
                className: cn(
                    base,
                    'question-set-player-tab--graded-correct !border-success/60 !bg-success/15 text-foreground shadow-none hover:!bg-success/25 hover:text-foreground dark:!bg-success/15 dark:hover:!bg-success/25'
                ),
            };
        }
        return {
            variant: 'outline',
            className: cn(
                base,
                'question-set-player-tab--graded-incorrect !border-destructive/60 !bg-destructive/15 text-foreground shadow-none hover:!bg-destructive/25 hover:text-foreground dark:!bg-destructive/15 dark:hover:!bg-destructive/25'
            ),
        };
    }
    if (answered) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'question-set-player-tab--answered !border-success/60 !bg-success/15 text-foreground shadow-none hover:!bg-success/25 hover:text-foreground dark:!bg-success/15 dark:hover:!bg-success/25'
            ),
        };
    }
    return {
        variant: 'outline',
        className: cn(
            base,
            'question-set-player-tab--unseen !border-foreground/25 !bg-muted/80 text-foreground shadow-sm hover:!bg-muted hover:!border-foreground/40 dark:!bg-muted/80'
        ),
    };
}

function getQuestionSetTabButtonProps(options: {
    isCurrent: boolean;
    answered: boolean;
    visited: boolean;
    useGradeColors: boolean;
    itemFullyCorrect: boolean;
}): { variant: 'default' | 'outline'; className: string } {
    const { isCurrent, answered, visited, useGradeColors, itemFullyCorrect } = options;
    const base = 'question-set-player-tab cursor-pointer min-w-9 transition-colors duration-200';

    if (useGradeColors) {
        if (isCurrent) {
            return {
                variant: 'default',
                className: cn(
                    base,
                    'question-set-player-tab--current question-set-player-tab--graded-current ring-2 ring-offset-2 ring-offset-background shadow-none',
                    itemFullyCorrect
                        ? 'ring-success/50 !border-success/80 !bg-success/10 hover:!bg-success/15'
                        : 'ring-destructive/50 !border-destructive/80 !bg-destructive/10 hover:!bg-destructive/15'
                ),
            };
        }
        if (itemFullyCorrect) {
            return {
                variant: 'outline',
                className: cn(
                    base,
                    'question-set-player-tab--graded-correct !border-success/60 !bg-success/15 text-foreground shadow-none hover:!bg-success/25 hover:text-foreground dark:!bg-success/15 dark:hover:!bg-success/25'
                ),
            };
        }
        return {
            variant: 'outline',
            className: cn(
                base,
                'question-set-player-tab--graded-incorrect !border-destructive/60 !bg-destructive/15 text-foreground shadow-none hover:!bg-destructive/25 hover:text-foreground dark:!bg-destructive/15 dark:hover:!bg-destructive/25'
            ),
        };
    }

    if (isCurrent) {
        return {
            variant: 'default',
            className: cn(
                base,
                'question-set-player-tab--current ring-2 ring-primary/40 ring-offset-2 ring-offset-background'
            ),
        };
    }
    if (answered) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'question-set-player-tab--answered !border-success/60 !bg-success/15 text-foreground shadow-none hover:!bg-success/25 hover:text-foreground dark:!bg-success/15 dark:hover:!bg-success/25'
            ),
        };
    }
    if (visited) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'question-set-player-tab--visited-unanswered !border-warning/60 !bg-warning/15 text-foreground shadow-none hover:!bg-warning/25 hover:text-foreground dark:!bg-warning/15 dark:hover:!bg-warning/25'
            ),
        };
    }
    return {
        variant: 'outline',
        className: cn(
            base,
            'question-set-player-tab--unseen border-border text-muted-foreground shadow-none hover:!bg-muted/40'
        ),
    };
}

type NestedElementPlayerConfig = NonNullable<ElementPlayerProps['config']>;

function toNestedPlayerConfig(value: ContentConfig | undefined): NestedElementPlayerConfig {
    return value as unknown as NestedElementPlayerConfig;
}

function resolveFitbBlankMode(
    item: QuestionSetItem,
    base: ContentConfig | undefined,
): 'dropdown' | 'type' | 'drag' {
    if (item.type === 'fitb-dropdown') {
        return 'dropdown';
    }
    const stored = (base as { blankMode?: string } | undefined)?.blankMode;
    if (stored === 'drag' || stored === 'dropdown' || stored === 'type') {
        return stored;
    }
    const hasWordBankPool = item.data.blanks?.some(
        (b) => (b.distractors?.length ?? 0) > 0,
    );
    if (hasWordBankPool) {
        return 'drag';
    }
    return 'type';
}

function mergeFitbPlayerConfig(
    base: ContentConfig | undefined,
    mode: 'dropdown' | 'type' | 'drag',
): NestedElementPlayerConfig {
    return toNestedPlayerConfig({
        ...(base ?? {}),
        blankMode: mode,
    } as ContentConfig);
}

export type { QuestionSetItemResult } from './scorer';

export interface QuestionSetResults {
    items: QuestionSetItemResult[];
    totalScore: number;
    max: number;
    percentage: number;
    passed: boolean;
}

export const QuestionSetPlayer: React.FC<QuestionSetPlayerProps> = ({
    data,
    config,
    onAnswer,
    answer: answerProp,
    result: resultProp,
    showFeedback: showFeedbackProp,
    isReview = false,
    disabled = false,
    className,
    language,
}) => {
    const questionOffset = (config as { questionOffset?: number } | undefined)?.questionOffset ?? 0;
    const scoringConfig = config?.scoring as QuestionSetScoringConfig | undefined;
    const scoringFingerprint = React.useMemo(
        () =>
            [
                scoringConfig?.partialCreditEnabled,
                scoringConfig?.pointsPerQuestion,
                scoringConfig?.passingThreshold,
                (scoringConfig as { max?: number } | undefined)?.max,
                (scoringConfig as { passingScore?: number } | undefined)?.passingScore,
            ].join('\0'),
        [
            scoringConfig?.partialCreditEnabled,
            scoringConfig?.pointsPerQuestion,
            scoringConfig?.passingThreshold,
            (scoringConfig as { max?: number } | undefined)?.max,
            (scoringConfig as { passingScore?: number } | undefined)?.passingScore,
        ]
    );
    const behaviorConfig = config?.behavior;
    const i18nConfig = config?.i18n;

    const { t } = useLocale({ config: i18nConfig, initialLanguage: language });

    const isMobilePlayer = useIsMobile(768);

    const shuffleChoices = behaviorConfig?.shuffleChoices ?? false;
    const showCorrectAnswer = behaviorConfig?.showCorrectAnswer ?? true;
    const instantFeedback = behaviorConfig?.instantFeedback ?? false;
    const examMode = behaviorConfig?.examMode ?? false;

    const [answers, setAnswers] = React.useState<Record<string, unknown>>(() =>
        answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)
            ? { ...answerProp }
            : {}
    );

    React.useEffect(() => {
        if (answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)) {
            setAnswers((prev) =>
                shallowAnswerMapsEqual(prev, answerProp as Record<string, unknown>)
                    ? prev
                    : { ...answerProp }
            );
        }
    }, [answerProp]);

    const [stepIndex, setStepIndex] = React.useState(0);
    const [visitedTabIndices, setVisitedTabIndices] = React.useState<Set<number>>(() => new Set());
    const childAnchorRefs = React.useRef<Map<string, HTMLElement>>(new Map());
    const [flashAnchorKey, setFlashAnchorKey] = React.useState<string | null>(null);
    const flashClearRef = React.useRef<number | null>(null);

    const clearFlashTimeout = React.useCallback(() => {
        if (flashClearRef.current != null) {
            window.clearTimeout(flashClearRef.current);
            flashClearRef.current = null;
        }
    }, []);

    React.useEffect(() => () => clearFlashTimeout(), [clearFlashTimeout]);

    const items = data?.items ?? EMPTY_QUESTION_SET_ITEMS;
    const itemIds = React.useMemo(() => map(items, (it) => it.id), [items]);
    const itemById = React.useMemo(
        () => new Map(map(items, (it) => [it.id, it] as const)),
        [items],
    );
    const { sidebarRows } = useCompositePlayerSidebarLayout(
        itemIds,
        data?.questionsSidebarRows,
    );
    const orderedItems = React.useMemo(
        () => orderItemsByQuestionsSidebarRows(items, sidebarRows),
        [items, sidebarRows],
    );
    const itemResultsRaw = resultProp?.details?.items;
    const itemResults = React.useMemo((): QuestionSetItemResult[] => {
        if (Array.isArray(itemResultsRaw) && itemResultsRaw.length > 0) {
            return itemResultsRaw as QuestionSetItemResult[];
        }
        const allowLocalGrade =
            (showFeedbackProp || isReview) &&
            data != null &&
            Array.isArray(data.items) &&
            data.items.length > 0;
        if (!allowLocalGrade) {
            return EMPTY_ITEM_RESULTS;
        }
        try {
            const graded = gradeQuestionSet(
                data,
                answers as Record<string, unknown>,
                scoringConfig
            );
            const itemsOut = graded.details?.items;
            if (Array.isArray(itemsOut) && itemsOut.length > 0) {
                return itemsOut as QuestionSetItemResult[];
            }
        } catch {
            return EMPTY_ITEM_RESULTS;
        }
        return EMPTY_ITEM_RESULTS;
    }, [itemResultsRaw, showFeedbackProp, isReview, items, answers, scoringFingerprint]);
    const submittedOrFeedback = Boolean(showFeedbackProp || isReview);
    // ELMS-2370: hide answer => no green/red grade colouring on the nav.
    const useGradeNavColors = submittedOrFeedback && showCorrectAnswer && itemResults.length > 0;

    const childNavEntries = React.useMemo(() => buildChildNavEntries(orderedItems), [orderedItems]);

    const labelByAnchorKey = React.useMemo(() => {
        const m = new Map<string, number>();
        childNavEntries.forEach((e, i) => {
            m.set(e.anchorKey, questionOffset + i + 1);
        });
        return m;
    }, [childNavEntries, questionOffset]);

    React.useEffect(() => {
        if (orderedItems.length === 0) {
            setStepIndex(0);
            return;
        }
        setStepIndex((i) => Math.min(Math.max(0, i), orderedItems.length - 1));
    }, [orderedItems.length]);

    React.useEffect(() => {
        if (orderedItems.length === 0) {
            return;
        }
        setVisitedTabIndices((prev) => {
            const next = new Set<number>();
            prev.forEach((i) => {
                if (i >= 0 && i < orderedItems.length) {
                    next.add(i);
                }
            });
            next.add(stepIndex);
            return next;
        });
    }, [stepIndex, orderedItems.length]);

    const layoutCfg = normalizeQuestionSetLayoutConfig(
        config?.layout as QuestionSetLayoutConfig | undefined,
    );
    const inlineSinglePageNavLayout = layoutCfg?.inlineSinglePageNav === true;
    const layoutType = layoutCfg.type;
    const displayModeRaw = layoutCfg?.displayMode ?? 'all';
    const displayMode = displayModeRaw === 'step' ? 'tabs' : displayModeRaw;

    const basePageContent = React.useMemo((): PageElementData | null => {
        if (!data) {
            return null;
        }
        return {
            ...defaultPageData,
            ...data.pageContent,
            sections: data.pageContent?.sections ?? defaultPageData.sections,
        };
    }, [data]);

    const [resolvedPageContent, setResolvedPageContent] = React.useState<PageElementData | null>(
        null,
    );

    React.useEffect(() => {
        setResolvedPageContent(basePageContent);
    }, [basePageContent]);

    React.useEffect(() => {
        const url = data?.passageUrl?.trim();
        const sections = basePageContent?.sections ?? [];
        if (!url || sections.length > 0) {
            return;
        }
        let cancelled = false;
        fetchPassageMarkdown(url)
            .then((markdown) => {
                if (cancelled || !basePageContent) return;
                setResolvedPageContent(mergePassageIntoPageContent(basePageContent, markdown));
            })
            .catch(() => {
                /* keep empty pageContent; passageUrl may be blocked by CORS in dev */
            });
        return () => {
            cancelled = true;
        };
    }, [data?.passageUrl, basePageContent]);

    const pageContent = resolvedPageContent ?? basePageContent;

    const hasPageContent = (pageContent?.sections?.length ?? 0) > 0;
    /** Horizontal side-by-side only when viewport is wide enough; stacks on small screens */
    const panelHorizontal =
        hasPageContent && layoutType === 'horizontal' && !isMobilePlayer;

    React.useEffect(() => {
        if (displayMode !== 'all') {
            childAnchorRefs.current.clear();
        }
    }, [displayMode]);

    const [singlePageNavPage, setSinglePageNavPage] = React.useState(0);

    React.useEffect(() => {
        if (childNavEntries.length <= 1) {
            setSinglePageNavPage(0);
            return;
        }
        const pageCount = Math.max(
            1,
            Math.ceil(childNavEntries.length / SINGLE_PAGE_NAV_PAGE_SIZE)
        );
        const maxPage = pageCount - 1;
        setSinglePageNavPage((p) => Math.min(Math.max(0, p), maxPage));
    }, [childNavEntries.length]);

    const attachChildAnchor = React.useCallback(
        (anchorKey: string, el: HTMLElement | null) => {
            if (displayMode !== 'all') {
                if (el == null) {
                    childAnchorRefs.current.delete(anchorKey);
                }
                return;
            }
            if (el != null) {
                childAnchorRefs.current.set(anchorKey, el);
            } else {
                childAnchorRefs.current.delete(anchorKey);
            }
        },
        [displayMode]
    );

    const handleJumpToChild = React.useCallback(
        (entryIndex: number) => {
            if (entryIndex < 0 || entryIndex >= childNavEntries.length) return;
            const entry = childNavEntries[entryIndex];
            if (displayMode === 'all') {
                setSinglePageNavPage(Math.floor(entryIndex / SINGLE_PAGE_NAV_PAGE_SIZE));
            }
            const el = childAnchorRefs.current.get(entry.anchorKey);
            if (el) {
                scrollElementToTopOffset(el, JUMP_TO_CHILD_SCROLL_TOP_OFFSET_PX);
            }
            clearFlashTimeout();
            setFlashAnchorKey(entry.anchorKey);
            flashClearRef.current = window.setTimeout(() => {
                setFlashAnchorKey(null);
                flashClearRef.current = null;
            }, 1300);
        },
        [childNavEntries, clearFlashTimeout, displayMode]
    );

    const mcqItemShellClass = React.useCallback(
        (typeClass: string, itemId: string) =>
            cn(
                'question-set-player-item',
                typeClass,
                'question-set-player-item-anchor scroll-mt-6 rounded-md',
                displayMode === 'all' &&
                flashAnchorKey === `${itemId}::mcq` &&
                'question-set-player-item-anchor--flash'
            ),
        [displayMode, flashAnchorKey]
    );

    const compositeItemShellClass = (typeClass: string) =>
        cn('question-set-player-item', typeClass, 'scroll-mt-6 rounded-md');

    const showSinglePageQuestionNav =
        displayMode === 'all' &&
        childNavEntries.length > 1 &&
        (panelHorizontal || (!hasPageContent && !isMobilePlayer));

    const singlePageNavRevision = React.useMemo(() => JSON.stringify(answers), [answers]);

    const singlePageNavEl = React.useMemo(() => {
        if (!showSinglePageQuestionNav) {
            return null;
        }
        const totalPages = Math.ceil(childNavEntries.length / SINGLE_PAGE_NAV_PAGE_SIZE);
        const pageStart = singlePageNavPage * SINGLE_PAGE_NAV_PAGE_SIZE;
        const pageEntries = childNavEntries.slice(pageStart, pageStart + SINGLE_PAGE_NAV_PAGE_SIZE);

        return (
            <div
                className="question-set-player-single-page-nav flex flex-wrap items-center justify-center gap-2"
                role="navigation"
                aria-label={t('questionNavigation', 'Question navigation')}
            >
                {totalPages > 1 ? (
                    <Button
                        type="button"
                        variant="default"
                        size="icon-sm"
                        className="question-set-player-single-page-nav-page-prev shrink-0 cursor-pointer border-2 border-primary shadow-md hover:opacity-[0.92] disabled:opacity-50"
                        aria-label={t('previousPage', 'Previous page')}
                        onClick={() => setSinglePageNavPage((p) => Math.max(0, p - 1))}
                        disabled={disabled || singlePageNavPage <= 0}
                    >
                        <ChevronLeft className="size-4 text-primary-foreground" strokeWidth={2.5} />
                    </Button>
                ) : null}
                <div className="question-set-player-single-page-nav-numbers flex flex-wrap justify-center gap-2">
                    {map(pageEntries, (entry, localIdx) => {
                        const globalIdx = pageStart + localIdx;
                        const tabProps = getQuestionSetChildNavButtonProps({
                            useGradeColors: useGradeNavColors,
                            answered: isNavEntryAnswered(entry, answers),
                            childCorrectWhenGraded: isChildNavEntryCorrectWhenGraded(
                                entry,
                                itemResults
                            ),
                        });
                        return (
                            <Button
                                key={entry.anchorKey}
                                type="button"
                                size="sm"
                                variant={tabProps.variant}
                                className={cn(
                                    tabProps.className,
                                    'question-set-player-single-page-nav-btn'
                                )}
                                onClick={() => handleJumpToChild(globalIdx)}
                                disabled={disabled}
                            >
                                {questionOffset + globalIdx + 1}
                            </Button>
                        );
                    })}
                </div>
                {totalPages > 1 ? (
                    <Button
                        type="button"
                        variant="default"
                        size="icon-sm"
                        className="question-set-player-single-page-nav-page-next shrink-0 cursor-pointer border-2 border-primary shadow-md hover:opacity-[0.92] disabled:opacity-50"
                        aria-label={t('nextPage', 'Next page')}
                        onClick={() => setSinglePageNavPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={disabled || singlePageNavPage >= totalPages - 1}
                    >
                        <ChevronRight className="size-4 text-primary-foreground" strokeWidth={2.5} />
                    </Button>
                ) : null}
            </div>
        );
    }, [
        showSinglePageQuestionNav,
        childNavEntries,
        answers,
        singlePageNavPage,
        disabled,
        handleJumpToChild,
        language,
        useGradeNavColors,
        itemResults,
    ]);

    const { inlineNav: inlineSinglePageNav } = useSinglePageNavPlacement(singlePageNavEl, {
        inlineSinglePageNav: inlineSinglePageNavLayout,
        revision: singlePageNavRevision,
    });

    const answeredChildCount = React.useMemo(
        () => filter(childNavEntries, (e) => isNavEntryAnswered(e, answers)).length,
        [childNavEntries, answers]
    );

    if (!data) {
        return (
            <div
                className={cn(
                    'question-set-player question-set-player--empty text-muted-foreground italic p-4',
                    className
                )}
            >
                {t('noQuestions', 'No questions available')}
            </div>
        );
    }

    const playerConfig =
        config === undefined ? undefined : (config as unknown as NestedElementPlayerConfig);
    const splitRatio = layoutCfg?.splitRatio;

    const handleItemAnswer = (itemId: string, itemAnswer: unknown) => {
        if (disabled) return;
        const next = { ...answers, [itemId]: itemAnswer };
        setAnswers(next);
        onAnswer?.(next);
    };

    const shouldShowFeedbackMcq = (item: QuestionSetItem, answer: unknown): boolean => {
        if (item.type !== 'mcq') return false;
        return examMode
            ? Boolean(showFeedbackProp)
            : instantFeedback
                ? isMcqAnswered(answer)
                : Boolean(showFeedbackProp);
    };

    const renderQuestion = (item: QuestionSetItem) => {
        const answer = answers[item.id];
        const itemResult = itemResults.find((r) => r.itemId === item.id);
        const resultForPlayer =
            itemResult != null
                ? {
                    isCorrect: itemResult.isCorrect,
                    score: itemResult.score,
                    max: itemResult.max,
                    details: itemResult.details,
                }
                : undefined;

        const elementChildLabels = buildElementChildLabels(item, labelByAnchorKey);

        switch (item.type) {
            case 'mcq': {
                const mergedData: MCQElementData = {
                    ...item.data,
                    shuffleChoices: shuffleChoices || item.data.shuffleChoices,
                };
                const showFb = shouldShowFeedbackMcq(item, answer);
                return (
                    <div
                        key={item.id}
                        ref={(el) => attachChildAnchor(`${item.id}::mcq`, el)}
                        className={mcqItemShellClass('question-set-player-item--mcq', item.id)}
                    >
                        <MCQPlayer
                            data={mergedData}
                            answer={answer as MCQAnswer}
                            onAnswer={(ans) => handleItemAnswer(item.id, ans)}
                            answerChoicesPresentation={
                                layoutCfg?.answerChoicesPresentation ?? 'standard'
                            }
                            disabled={disabled}
                            showFeedback={showFb && showCorrectAnswer}
                            result={resultForPlayer}
                            isReview={isReview}
                            config={playerConfig}
                            elementChildLabels={elementChildLabels}
                            language={language}
                        />
                    </div>
                );
            }
            case 'match': {
                const showFb = examMode
                    ? Boolean(showFeedbackProp)
                    : instantFeedback
                        ? isMatchAnswered(item.data, answer)
                        : Boolean(showFeedbackProp);
                const matchPrefix = `${item.id}::match::`;
                const matchFlashRowId =
                    flashAnchorKey != null && flashAnchorKey.startsWith(matchPrefix)
                        ? flashAnchorKey.slice(matchPrefix.length)
                        : null;
                return (
                    <div
                        key={item.id}
                        className={compositeItemShellClass('question-set-player-item--match')}
                    >
                        <MatchPlayer
                            data={item.data}
                            answer={(answer as MatchAnswer) ?? {}}
                            onAnswer={(ans) => handleItemAnswer(item.id, ans)}
                            disabled={disabled}
                            showFeedback={showFb && showCorrectAnswer}
                            result={resultForPlayer}
                            isReview={isReview}
                            config={playerConfig}
                            elementChildLabels={elementChildLabels}
                            onMatchItemAnchor={(rowId, el) =>
                                attachChildAnchor(`${item.id}::match::${rowId}`, el)
                            }
                            highlightMatchItemId={matchFlashRowId}
                            language={language}
                        />
                    </div>
                );
            }
            case 'fitb-dropdown':
            case 'fitb-type': {
                const mode = resolveFitbBlankMode(item, config);
                const fitbCfg = mergeFitbPlayerConfig(config, mode);
                const showFb = examMode
                    ? Boolean(showFeedbackProp)
                    : instantFeedback
                        ? isFitbAnswered(item.data, answer)
                        : Boolean(showFeedbackProp);
                const fitbPrefix = `${item.id}::fitb::`;
                const fitbFlashBlankId =
                    flashAnchorKey != null && flashAnchorKey.startsWith(fitbPrefix)
                        ? flashAnchorKey.slice(fitbPrefix.length)
                        : null;
                return (
                    <div
                        key={item.id}
                        className={compositeItemShellClass('question-set-player-item--fitb')}
                    >
                        <FITBPlayer
                            data={item.data}
                            answer={(answer as FITBAnswer) ?? {}}
                            onAnswer={(ans) => handleItemAnswer(item.id, ans)}
                            disabled={disabled}
                            showFeedback={showFb && showCorrectAnswer}
                            result={resultForPlayer}
                            isReview={isReview}
                            config={fitbCfg}
                            elementChildLabels={elementChildLabels}
                            onFitbBlankAnchor={(blankId, el) =>
                                attachChildAnchor(`${item.id}::fitb::${blankId}`, el)
                            }
                            flashBlankId={fitbFlashBlankId}
                            language={language}
                        />
                    </div>
                );
            }
            case 'ordering': {
                const showFb = examMode
                    ? Boolean(showFeedbackProp)
                    : instantFeedback
                        ? isOrderingAnswered(item.data, answer)
                        : Boolean(showFeedbackProp);
                return (
                    <div
                        key={item.id}
                        ref={(el) => attachChildAnchor(`${item.id}::ordering`, el)}
                        className={compositeItemShellClass('question-set-player-item--ordering')}
                    >
                        <OrderingPlayer
                            data={item.data}
                            answer={(answer as OrderingAnswer) ?? []}
                            onAnswer={(ans) => handleItemAnswer(item.id, ans)}
                            disabled={disabled}
                            showFeedback={showFb && showCorrectAnswer}
                            result={resultForPlayer}
                            isReview={isReview}
                            config={playerConfig}
                            language={language}
                        />
                    </div>
                );
            }
        }
    };

    const pageBlock = hasPageContent && pageContent ? (
        <div className="question-set-player-page">
            <PagePlayer
                data={pageContent}
                submitted={submittedOrFeedback}
                className="question-set-player-page-inner"
            />
        </div>
    ) : null;

    const progressBlock =
        !submittedOrFeedback && childNavEntries.length > 0 ? (
            <div className="question-set-player-progress space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{t('progress', 'Progress')}</span>
                    <span>
                        {answeredChildCount} / {childNavEntries.length} {t('answered', 'answered')}
                    </span>
                </div>
                <Progress
                    value={
                        childNavEntries.length > 0
                            ? (answeredChildCount / childNavEntries.length) * 100
                            : 0
                    }
                />
            </div>
        ) : null;

    const emptyQuestionsMessage =
        items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
                {t('noQuestions', 'No questions available')}
            </p>
        ) : null;

    let questionsBody: React.ReactNode = null;
    if (items.length > 0) {
        if (displayMode === 'all') {
            questionsBody = (
                <CompositePlayerGroupedItemsBody
                    prefix="question-set-player"
                    itemIds={itemIds}
                    questionsSidebarRows={data.questionsSidebarRows}
                    renderItem={(id) => {
                        const item = itemById.get(id);
                        return item ? renderQuestion(item) : null;
                    }}
                />
            );
        } else {
            questionsBody = (
                <div className="question-set-player-questions question-set-player-questions--tabs flex flex-col gap-6">
                    {submittedOrFeedback ? (
                        <div className="question-set-player-questions question-set-player-questions--tabs-review space-y-8">
                            {map(orderedItems, (item) => renderQuestion(item))}
                        </div>
                    ) : (
                        <>
                            {orderedItems[stepIndex] ? renderQuestion(orderedItems[stepIndex]) : null}
                            {orderedItems.length > 1 ? (
                                <div
                                    className="question-set-player-tab-row question-set-player-tab-row--bottom flex flex-wrap justify-center gap-2 border-t border-border pt-4"
                                    role="tablist"
                                >
                                    {map(orderedItems, (item, idx) => {
                                        const itemResult = itemResults.find(
                                            (r) => r.itemId === item.id
                                        );
                                        const tabProps = getQuestionSetTabButtonProps({
                                            isCurrent: idx === stepIndex,
                                            answered: isItemAnswered(item, answers[item.id]),
                                            visited: visitedTabIndices.has(idx),
                                            useGradeColors: useGradeNavColors,
                                            itemFullyCorrect: itemResult?.isCorrect === true,
                                        });
                                        return (
                                            <Button
                                                key={item.id}
                                                type="button"
                                                size="sm"
                                                variant={tabProps.variant}
                                                className={tabProps.className}
                                                aria-selected={idx === stepIndex}
                                                onClick={() => setStepIndex(idx)}
                                                disabled={disabled}
                                            >
                                                {questionOffset + idx + 1}
                                            </Button>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            );
        }
    }

    const questionsScrollableBlock = (
        <>
            {emptyQuestionsMessage}
            {/* {progressBlock} */}
            {questionsBody}
        </>
    );

    const questionsColumn = (
        <div
            className={cn(
                'question-set-player-questions-column min-w-0 flex flex-col',
                panelHorizontal && 'min-h-0 flex-1'
            )}
        >
            {panelHorizontal ? (
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain border-l border-border pl-4 lg:pl-6">
                    <div className="space-y-6 pr-1">{questionsScrollableBlock}</div>
                </div>
            ) : (
                <div className="space-y-6">{questionsScrollableBlock}</div>
            )}
        </div>
    );
    let mainBody: React.ReactNode;
    if (panelHorizontal) {
        mainBody = (
            <ContentPlayerSplitLayout
                prefix="question-set-player-split"
                splitRatio={splitRatio}
                leftPanel={
                    <div className="question-set-player-split-page question-set-player-split-page--scroll min-w-0 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pr-1">
                        {pageBlock}
                    </div>
                }
                rightPanel={
                    <div className="question-set-player-split-questions question-set-player-split-questions--stack min-w-0 min-h-0 flex flex-col overflow-hidden">
                        {questionsColumn}
                    </div>
                }
                footer={inlineSinglePageNav}
            />
        );
    } else {
        mainBody = (
            <>
                {pageBlock}
                {questionsColumn}
                {inlineSinglePageNav}
            </>
        );
    }

    return (
        <div
            className={cn(
                'question-set-player',
                panelHorizontal
                    ? 'question-set-player--horizontal-split flex flex-col min-h-0 flex-1 gap-6'
                    : 'space-y-6',
                className
            )}
        >
            {data.instructions ? (
                <div className="question-set-player-header question-set-player-header--split flex-shrink-0 space-y-2">
                    <p className="text-sm text-muted-foreground">{data.instructions}</p>
                </div>
            ) : null}

            {mainBody}
        </div>
    );
};

export default QuestionSetPlayer;
